import { Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PatronDetailFragment,
  PaymentMethod,
  PendingPurchaseItemStatus,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { Organism } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'

type MembershipCardProps = {
  membership: PatronDetailFragment
  width: number
}

/**
 * MembershipCard
 * 会員番号が付与されたメンバーシップを表示するカード
 */

export const MembershipCard: Organism<MembershipCardProps> = ({
  membership,
  width,
}) => {
  const month = useMemo(
    () => DateTime.calculateMonthsSince(membership?.formattedCreatedAt),
    [membership]
  )

  const monthSuffix = month > 1 ? '_other' : '_one'

  const currentProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      membership.artist,
      membership.subscription?.planId ?? ''
    )
  }, [membership.artist, membership.subscription?.planId])

  const downgradeProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      membership.artist,
      membership.subscription?.downgradePlanId ?? ''
    )
  }, [membership.artist, membership.subscription?.downgradePlanId])

  const upgradeProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      membership.artist,
      membership.subscription?.upgradePlanId ?? ''
    )
  }, [membership.artist, membership.subscription?.upgradePlanId])

  const membershipState = useMemo(() => {
    const pendingPurchasePatron = membership.artist?.pendingPurchasePatron
    if (pendingPurchasePatron?.metadata?.membership?.planId) {
      if (
        pendingPurchasePatron?.status ===
        PendingPurchaseItemStatus.WaitingPayment
      ) {
        return 'KONBINI_WAITING_PAYMENT'
      }
      return
    }
    if (
      membership.type === 'escott' &&
      !membership.artist?.subscription?.carrierPayment &&
      (membership.cancelAtPeriodEnd ||
        membership.upgradeAtNextPeriod ||
        membership.downgradeAtNextPeriod)
    ) {
      return 'CARRIER_INACTIVE'
    }
    if (
      membership.subscription?.downgradePlanId ||
      membership.upgradeAtNextPeriod // 次回更新日にアップグレードする場合
    ) {
      return 'CHANGED_PLAN'
    }
    if (membership.cancelAtPeriodEnd) {
      return 'RESUME'
    }
    return 'ACTIVE'
  }, [
    membership.artist?.pendingPurchasePatron,
    membership.cancelAtPeriodEnd,
    membership.subscription?.downgradePlanId,
  ])

  const { t } = useTranslation('userProfile')

  return (
    <Card sx={{ borderRadius: 2, width }}>
      <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
        <Chip
          size="small"
          sx={{
            height: 20,
            borderRadius: '2px',
            minWidth: 40,
            bgcolor: 'primary.main',
            '.MuiChip-label': {
              px: 1,
            },
          }}
          label={
            <Typography
              color="primary.contrastText"
              variant="overline"
              display="block"
            >
              {t('inProgress', '加入中')}
            </Typography>
          }
        />
        <Stack py={2} spacing={4} direction="row">
          <Typography variant="h5" sx={styleSets.lineCamp(2)}>
            {currentProduct?.title}
          </Typography>
        </Stack>
        <Stack py={2} spacing={4} direction="row">
          <Typography variant="subtitle2" sx={styleSets.lineCamp(2)}>
            {t('membershipNumber', '会員番号')}
          </Typography>
          <Typography variant="caption" sx={styleSets.lineCamp(2)}>
            {membership.formattedMembershipNumber}
          </Typography>
        </Stack>
        <Stack py={2} spacing={4} direction="row">
          <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
            {t('membershipPeriod', '継続期間')}
          </Typography>
          {/* 継続中 */}
          {membershipState === 'ACTIVE' && (
            <Typography variant="caption" align="left">
              {t(
                `${
                  membership?.paymentMethod || PaymentMethod.CreditCard
                }AutoUpdate${monthSuffix}`,
                {
                  count: month,
                  expiredAt: membership?.formattedExpiredAt,
                }
              )}
            </Typography>
          )}
          {/* プラン変更予定(次回更新日に切り替わる) */}
          {membershipState === 'CHANGED_PLAN' && (
            <Typography variant="caption" align="left">
              {t('changeNote', {
                count: month,
                productTitle: downgradeProduct?.title || upgradeProduct?.title,
                expiredAt: membership?.formattedExpiredAt,
              })}
            </Typography>
          )}
          {/* コンビニ決済待ち*/}
          {membershipState === 'KONBINI_WAITING_PAYMENT' && (
            <Typography variant="caption" align="left" color="text.hint">
              {t('konbiniJoinedNote')}
            </Typography>
          )}
          {/* 解約予定 */}
          {(membershipState === 'RESUME' ||
            membershipState === 'CARRIER_INACTIVE') && (
            <Typography variant="caption" align="left">
              {t('cancelNote', {
                count: month,
                expiredAt: membership?.formattedExpiredAt,
              })}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
